/// <reference types="vite/client" />
import { initProStudioUrqlGql } from '@pypestream/api-services/urql';

import { sendUserMessage } from '../../app.xstate-utils';
import { reportDegradedLoad } from './report-degraded-load';
import { getCustomerPypes } from '../../../utils';
// Imported from the FILE, not the `utils` barrel (which the line above does use):
// the unit tests around these helpers stub the barrel WHOLESALE, so a barrel
// import would hand them `undefined` instead of the real predicate. Same shape as
// publish-bot-version.ts and mutate-widget-app-design.ts.
import { checkForErrors } from '../../../utils/check-for-errors';
import { solutionStartChatIds } from '../../../utils/resolve-bot-solution-id';

/**
 * One id or several — a legacy solution's streams carry either its `botSolutionId`
 * (pre-migration channels, never rewritten) or `<customerName>.<uuid>` (every channel
 * created since), so matching takes the whole set. See `resolveBotSolutionIds`.
 */
type StartChatIds = string | readonly string[] | null | undefined;

const toIdSet = (ids: StartChatIds): Set<string> =>
  new Set((typeof ids === 'string' ? [ids] : (ids ?? [])).filter(Boolean));
import { ProStudioContext } from '../../prostudio.xstate.types';
import { Pype } from '../../prostudio.api-service.types';

export interface UpdateSurveyBotStreamsParams {
  bots: ProStudioContext['solutions'];
  eventBots: string[] | undefined;
  environment: string;
  uniqueCustomerId: string;
  projectId: string;
  /** The survey's own `<customerName>.<uuid>` — stamped as each stream's `end_chat_bot_id`. */
  botId: string;
  /** `ctx.customerId` — the customer NAME the mains' stream ids are built from. */
  customerName: string;
  userInfo: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
}

export const updateSurveyBotStreams = async ({
  bots,
  eventBots,
  environment,
  uniqueCustomerId,
  projectId,
  botId,
  customerName,
  userInfo,
}: UpdateSurveyBotStreamsParams): Promise<void> => {
  const urqlGql = initProStudioUrqlGql({
    userId: userInfo.id,
    userEmail: userInfo.email,
    userFirstName: userInfo.firstName,
    userLastName: userInfo.lastName,
  });

  const { live, sandbox } = await getCustomerPypes({
    uniqueCustomerId,
    projectId,
  });

  const envPypes = environment === 'live' ? live : sandbox;

  // A main's streams carry `<customerName>.<uuid>` as `start_chat_bot_id` — what the
  // backend's createChannel stamps (getSolutionStartChatId) — or, for a stream that
  // predates the migration, the legacy `botSolutionId`, which the backend never
  // rewrote. Either identifies the main.
  const selectedBots = (
    bots?.filter(
      // Match on `botType !== 'survey'`, NOT `=== 'main'` — same rationale as
      // getSurveyLinkedSolutionIds below: `botType` is absent from getSolutions
      // and only populated lazily once a solution is visited, so a `=== 'main'`
      // filter drops an unvisited main (botType undefined) and the survey→main
      // link is never written. `eventBots` already restricts to the picked
      // solution(s), and a stream's start_chat_bot_id is always a main's
      // start-chat id, so excluding only the survey itself is correct.
      (bot) =>
        bot.botType !== 'survey' && bot.id && (eventBots || []).includes(bot.id)
    ) || []
  ).map((bot) => ({
    ids: toIdSet(solutionStartChatIds(customerName, bot)),
    type: bot.botType,
  }));

  const promises = envPypes
    ?.flatMap((pype) => {
      const { streams } = pype;

      const res =
        selectedBots?.flatMap(({ ids }) => {
          const result =
            streams?.map((stream) => {
              // Written back as the stream already has it: rewriting a legacy
              // stream's start id would detach it from the bot it serves.
              const startChatBotId = stream?.['start_chat_bot_id'];

              const updateStreamBot =
                environment === 'live'
                  ? urqlGql.updateStreamBotLive
                  : urqlGql.updateStreamBotSandbox;

              if (startChatBotId && ids.has(startChatBotId)) {
                return updateStreamBot({
                  pypeId: pype.id || '',
                  streamId: stream.id || '',
                  customerId: uniqueCustomerId,
                  endChatBotId: botId,
                  startChatBotId: startChatBotId || '',
                  projectId,
                });
              }

              return null;
            }) || [];

          return result;
        }) || [];

      return res;
    })
    .filter(Boolean);

  await Promise.all(promises);
};

/**
 * Reverse of {@link updateSurveyBotStreams}: given one environment's pypes, find every
 * main solution this survey bot is linked to. A stream links them when
 * `end_chat_bot_id` is one of the survey's ids; its `start_chat_bot_id` is one of the
 * main's (see `solutionStartChatIds` — uuid form or legacy `botSolutionId`), mapped back to the solution
 * `id` (the publish modal's <Select> value).
 *
 * NB: we match on `botType !== 'survey'`, NOT `=== 'main'`, on purpose. `botType` is
 * absent from `getSolutions` and only populated lazily once a solution is visited, so a
 * `=== 'main'` filter would drop unvisited mains and the modal would show no linked
 * solutions on a cold app load (until each main page is visited). A stream's
 * `start_chat_bot_id` is always a main's start-chat id, so matching by id and merely
 * excluding the survey itself is both correct and visit-independent.
 */
export const getSurveyLinkedSolutionIds = (
  pypes: Pype[] | undefined,
  surveyBotIds: StartChatIds,
  {
    bots,
    customerName,
  }: {
    bots: ProStudioContext['solutions'];
    /** `ctx.customerId` — the customer NAME the mains' stream ids are built from. */
    customerName: string | null | undefined;
  }
): string[] => {
  const surveyIds = toIdSet(surveyBotIds);
  if (!surveyIds.size || !bots?.length) {
    return [];
  }

  const linkedStartChatBotIds = new Set(
    (pypes ?? [])
      .flatMap((pype) => pype.streams ?? [])
      .filter(
        (stream) =>
          !!stream?.end_chat_bot_id && surveyIds.has(stream.end_chat_bot_id)
      )
      .map((stream) => stream?.start_chat_bot_id)
      .filter((id): id is string => Boolean(id))
  );

  const linkedIds = bots
    .filter(
      (bot) =>
        bot.botType !== 'survey' &&
        !!bot.id &&
        solutionStartChatIds(customerName, bot).some((id) =>
          linkedStartChatBotIds.has(id)
        )
    )
    .map((bot) => bot.id as string);

  return [...new Set(linkedIds)];
};

/**
 * Environments (lowercased, e.g. `['sandbox', 'live']`) where a given main bot has a
 * channel actually bound to it — i.e. a pype stream with
 * `start_chat_bot_id` among the main's ids. This is the exact precondition
 * {@link updateSurveyBotStreams} needs to persist a survey→main link: it only updates
 * an existing stream whose `start_chat_bot_id` matches the main. A solution that is
 * merely "published" but has no such bound stream (no channel in that env at all) is
 * therefore NOT linkable, and this returns the env without it.
 *
 * NB: a channel created AFTER the survey was published is NOT such a case — the backend
 * gives it a stream carrying `start_chat_bot_id`, so the main stays linkABLE; what that
 * channel lacks is the `end_chat_bot_id` stamp, which is a different defect and is what
 * {@link planSurveyLinkInheritance} repairs (PSTUDIO-3928). An earlier version of this
 * comment listed it here, which read as "the main disappears from the modal" and is wrong.
 */
export const getBotChannelEnvs = (
  pypesByEnv: { live?: Pype[]; sandbox?: Pype[] },
  botSolutionIds: StartChatIds
): string[] => {
  const ids = toIdSet(botSolutionIds);
  if (!ids.size) {
    return [];
  }

  const hasBindStream = (pypes: Pype[] | undefined): boolean =>
    (pypes ?? []).some((pype) =>
      (pype.streams ?? []).some(
        (stream) =>
          !!stream?.start_chat_bot_id && ids.has(stream.start_chat_bot_id)
      )
    );

  const envs: string[] = [];
  if (hasBindStream(pypesByEnv.sandbox)) {
    envs.push('sandbox');
  }
  if (hasBindStream(pypesByEnv.live)) {
    envs.push('live');
  }

  return envs;
};

/** One stream that must be stamped with the survey link its siblings already carry. */
export interface SurveyLinkInheritance {
  env: 'live' | 'sandbox';
  pypeId: string;
  streamId: string;
  startChatBotId: string;
  endChatBotId: string;
}

/**
 * The survey→main link lives on a pype STREAM (`start_chat_bot_id` = the main's
 * `botSolutionId`, `end_chat_bot_id` = the survey's `botId`) and is written ONLY by
 * {@link updateSurveyBotStreams}, i.e. when the SURVEY is published — it stamps the streams
 * that exist at that moment. A channel created AFTER that publish therefore gets a stream
 * with an empty `end_chat_bot_id` and runs no survey until the survey is republished
 * (PSTUDIO-3928). This plans the missing writes: for a given main, every stream that has no
 * survey yet INHERITS the one its already-linked siblings carry.
 *
 * Environments are computed INDEPENDENTLY (`pype.env`, set in get-customer-pypes.ts): live
 * and sandbox hold different survey bot ids, so a live link must never leak into sandbox or
 * vice versa. Within one env we only inherit FROM streams that are already linked — if the
 * env has none, there is nothing to copy and the plan for it is empty.
 *
 * `envScope` narrows the plan to a SINGLE env and is what the channel-create path passes:
 * the read covers both envs, so without it creating a Sandbox channel would also stamp an
 * unlinked LIVE stream — an action in sandbox reaching production. Omit it only for a caller
 * that genuinely means "heal every env".
 *
 * When the linked streams disagree (several surveys bound to the same main across channels),
 * the winner is the most frequent value and, on a tie, the lexicographically smallest. The
 * choice is deterministic ON PURPOSE: the same input must always yield the same write, so the
 * call is idempotent and safe to replay (a re-created channel, a retried invoke) instead of
 * flip-flopping a channel between two surveys.
 *
 * Streams missing a usable `pype.id` / `stream.id` are skipped — they cannot be addressed by
 * the update mutation.
 */
export const planSurveyLinkInheritance = (
  pypes: Pype[] | undefined,
  mainBotSolutionIds: StartChatIds,
  envScope?: SurveyLinkInheritance['env']
): SurveyLinkInheritance[] => {
  const mainIds = toIdSet(mainBotSolutionIds);
  if (!mainIds.size || !pypes?.length) {
    return [];
  }

  interface EnvBucket {
    /** `end_chat_bot_id`s already bound to this main in this env — what we inherit FROM. */
    linkedSurveyIds: string[];
    /** Streams of this main in this env with no survey yet — what we write TO. */
    unlinked: Array<{
      pypeId: string;
      streamId: string;
      startChatBotId: string;
    }>;
  }

  // Keyed by env, insertion-ordered, so the emitted plan is stable for a stable input.
  const buckets = new Map<SurveyLinkInheritance['env'], EnvBucket>();

  pypes.forEach((pype) => {
    const env: Pype['env'] | undefined = pype?.env;
    if (env !== 'live' && env !== 'sandbox') {
      return;
    }

    // Scoped to ONE env by the caller: creating a Sandbox channel must not write
    // into a Live stream. Without this the plan spans every env the read covers,
    // so an action taken in sandbox silently reaches production.
    if (envScope && env !== envScope) {
      return;
    }

    let bucket = buckets.get(env);
    if (!bucket) {
      bucket = { linkedSurveyIds: [], unlinked: [] };
      buckets.set(env, bucket);
    }

    (pype.streams ?? []).forEach((stream) => {
      if (
        !stream?.start_chat_bot_id ||
        !mainIds.has(stream.start_chat_bot_id)
      ) {
        return;
      }

      const endChatBotId = stream?.end_chat_bot_id;
      if (endChatBotId) {
        bucket?.linkedSurveyIds.push(endChatBotId);
        return;
      }

      const pypeId = pype.id;
      const streamId = stream?.id;
      if (!pypeId || !streamId) {
        return;
      }

      // The stream's own start id, not a rebuilt one: a legacy stream keeps its
      // `botSolutionId`, and rewriting it would detach the stream from its bot.
      bucket?.unlinked.push({
        pypeId,
        streamId,
        startChatBotId: stream.start_chat_bot_id,
      });
    });
  });

  const plan: SurveyLinkInheritance[] = [];

  buckets.forEach((bucket, env) => {
    if (!bucket.linkedSurveyIds.length || !bucket.unlinked.length) {
      return;
    }

    const counts = new Map<string, number>();
    bucket.linkedSurveyIds.forEach((id) => {
      counts.set(id, (counts.get(id) ?? 0) + 1);
    });

    const [endChatBotId] = [...counts.entries()].sort(
      ([idA, countA], [idB, countB]) =>
        countB - countA || (idA < idB ? -1 : idA > idB ? 1 : 0)
    )[0];

    bucket.unlinked.forEach(({ pypeId, streamId, startChatBotId }) => {
      plan.push({
        env,
        pypeId,
        streamId,
        startChatBotId,
        endChatBotId,
      });
    });
  });

  return plan;
};

export interface ApplySurveyLinkInheritanceParams {
  plan: SurveyLinkInheritance[];
  uniqueCustomerId: string;
  projectId: string;
  userInfo: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
}

/**
 * Executes a {@link planSurveyLinkInheritance} plan through the very same mutations
 * {@link updateSurveyBotStreams} uses, so both paths carry identical audit headers.
 *
 * BEST-EFFORT ON PURPOSE: the caller has already created the channel, so a failure here must
 * not turn a successful creation into an error — the missing link is recoverable (republish
 * the survey, or the next run of this plan). Hence `Promise.allSettled` + a `console.warn`
 * carrying the stream that could not be stamped, and a count of the ones that were.
 *
 * A resolved promise is NOT a success: an urql mutation resolves even when the server refuses
 * it — the failure arrives as `result.error` (or as an `errors` array inside `data`) on the
 * RESOLVED value and is never thrown, so `Promise.allSettled` reports it as `fulfilled`. The
 * repo's own notion of "this response failed" is `checkForErrors`, which is reused here rather
 * than re-invented; it THROWS, so it is called inside a try/catch — this path must stay
 * best-effort and never propagate.
 *
 * @returns how many streams were actually stamped.
 */
export const applySurveyLinkInheritance = async ({
  plan,
  uniqueCustomerId,
  projectId,
  userInfo,
}: ApplySurveyLinkInheritanceParams): Promise<number> => {
  if (!plan.length) {
    return 0;
  }

  const urqlGql = initProStudioUrqlGql({
    userId: userInfo.id,
    userEmail: userInfo.email,
    userFirstName: userInfo.firstName,
    userLastName: userInfo.lastName,
  });

  const results = await Promise.allSettled(
    plan.map((entry) => {
      const updateStreamBot =
        entry.env === 'live'
          ? urqlGql.updateStreamBotLive
          : urqlGql.updateStreamBotSandbox;

      return updateStreamBot({
        pypeId: entry.pypeId,
        streamId: entry.streamId,
        customerId: uniqueCustomerId,
        endChatBotId: entry.endChatBotId,
        startChatBotId: entry.startChatBotId,
        projectId,
      });
    })
  );

  let stamped = 0;
  results.forEach((result, index) => {
    let failure: unknown;

    if (result.status === 'rejected') {
      failure = result.reason;
    } else {
      try {
        checkForErrors(result.value ?? {});
      } catch (error) {
        failure = error;
      }
    }

    if (!failure) {
      stamped += 1;
      return;
    }

    const entry = plan[index];
    console.warn(
      `Failed to inherit the survey link for stream ${entry.streamId} (pype ${entry.pypeId}, env ${entry.env}, main ${entry.startChatBotId}, survey ${entry.endChatBotId})`,
      failure
    );
    // Best-effort by design, and therefore invisible by design: the caller has
    // already resolved, so nothing downstream can notice a stream that was not
    // stamped. Identifiers only — no bot content.
    reportDegradedLoad(failure, 'survey-link-inheritance:stream', {
      streamId: entry.streamId,
      pypeId: entry.pypeId,
      env: entry.env,
    });
  });

  return stamped;
};

const FILE_UPLOAD_FAILED_MESSAGE =
  'Please upload the files manually from the Supervisor config panel.';

/** What the toast may carry is short: a status, or a network error message. */
const MAX_REASON_LENGTH = 200;

/** Long enough to read the status and the way out; the bare 3s of the old toast was not. */
const FILE_UPLOAD_TOAST_MS = 10_000;

/**
 * How long the create wizard's upload may go unanswered. These are the user's
 * own knowledge files, larger than a key or a script, so the bound is wider
 * than the knowledge setup's one-minute upload bound (`UPLOAD_TIMEOUT_MS` in
 * `pages/talk`). It covers the whole request, not inactivity: a link too slow
 * to send the files in this time is cut off although the server may have kept
 * them. Without any bound, a connection that never answered left this invoke
 * pending and no reason toast ever appeared.
 */
export const UPLOAD_FILES_TIMEOUT_MS = 120_000;

const fileUploadFailedText = (reason: string): string => {
  const trimmed = reason.trim();
  const shown =
    trimmed.length > MAX_REASON_LENGTH
      ? `${trimmed.slice(0, MAX_REASON_LENGTH)}…`
      : trimmed;
  return shown
    ? `File upload failed (${shown}). ${FILE_UPLOAD_FAILED_MESSAGE}`
    : `File upload failed. ${FILE_UPLOAD_FAILED_MESSAGE}`;
};

/** Candidate's toast, shown with Bobcat's master flag off (DoD #2). */
const CANDIDATE_FILE_UPLOAD_FAILED_TOAST = {
  text: `File upload failed. ${FILE_UPLOAD_FAILED_MESSAGE}`,
  autoClose: 3000,
};

/**
 * `bobcat`: Bobcat's master flag (`bobcat-five-stage-nav`). Only with it on is
 * the request bounded and the toast says why; off, this is candidate's upload.
 */
export const uploadSolutionFiles = async (
  solutionVersionId: string,
  files: File[],
  { bobcat = false }: { bobcat?: boolean } = {}
): Promise<void> => {
  const uploadUrl = `${import.meta.env.FE_ADMIN_URL}/rest/solution-version-upload-files/${solutionVersionId}`;
  const formData = new FormData();

  files.forEach((file) => {
    formData.append('files', file);
  });

  const controller = bobcat ? new AbortController() : undefined;
  let timedOut = false;
  const timer = controller
    ? setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, UPLOAD_FILES_TIMEOUT_MS)
    : undefined;

  try {
    const response = await fetch(uploadUrl, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      signal: controller?.signal,
    });

    if (!response.ok) {
      sendUserMessage({
        type: 'error',
        // The status only, and the body is never read: on candidate a refusal
        // is an AWS IAM message naming the backend's service user, the account
        // id and the bucket path, which is not for the user's screen
        // (PSTUDIO-4455, decided 2026-10-18 together with #3039).
        ...(bobcat
          ? {
              text: fileUploadFailedText(String(response.status)),
              autoClose: FILE_UPLOAD_TOAST_MS,
            }
          : CANDIDATE_FILE_UPLOAD_FAILED_TOAST),
      });
      // The toast tells the user to upload the files by hand; it tells us
      // nothing, because this helper resolves either way and the message goes
      // out through `sendUserMessage` directly rather than the machine's
      // `showError` event.
      reportDegradedLoad(
        new Error(`solution file upload failed with ${response.status}`),
        'upload-solution-files',
        { solutionVersionId, status: response.status, fileCount: files.length }
      );

      return;
    }
  } catch (caught) {
    // Our own abort surfaces as an `AbortError` that says nothing: name what
    // happened instead.
    const error = timedOut
      ? new Error(
          `The server did not answer in ${UPLOAD_FILES_TIMEOUT_MS / 1000}s.`
        )
      : caught;
    sendUserMessage({
      type: 'error',
      ...(bobcat
        ? {
            text: fileUploadFailedText(
              error instanceof Error ? error.message : String(error)
            ),
            autoClose: FILE_UPLOAD_TOAST_MS,
          }
        : CANDIDATE_FILE_UPLOAD_FAILED_TOAST),
    });
    reportDegradedLoad(error, 'upload-solution-files', {
      solutionVersionId,
      fileCount: files.length,
    });
  } finally {
    clearTimeout(timer);
  }
};
